import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DegradedBar } from './components/DegradedBar.js';
import { DemoBar } from './components/DemoBar.js';
import { Header } from './components/Header.js';
import { Icon } from './components/Icon.js';
import { useBoard, useLogout, useSession } from './api/hooks.js';
import { directionOf, runPageTransition, type NavDirection } from './lib/pageTransition.js';
import { canOpen, homeView, isReadonly, VIEW_NAMES, type ViewName } from './lib/roles.js';
import { BoardProvider, useBoardData } from './state/board.js';
import { AutoUpdate } from './state/autoUpdate.js';
import { useClock } from './state/clock.js';
import { useRemoteChangeNotices } from './state/notices.js';
import { useUi } from './state/ui.js';
import { AdminView } from './views/AdminView.js';
import { BoardView } from './views/BoardView.js';
import { ClerkView } from './views/ClerkView.js';
import { LoginView } from './views/LoginView.js';
import { MonitorView } from './views/MonitorView.js';
import { NurseView } from './views/NurseView.js';
import { RxView } from './views/RxView.js';
import { StatsView } from './views/StatsView.js';

/** URL のパス（/clerk など）から画面名を読む。旧形式の #clerk も受け付け、パスへ置き換える。 */
const readPath = (): ViewName | null => {
  const seg = window.location.pathname.split('/').filter(Boolean)[0] ?? '';
  if ((VIEW_NAMES as string[]).includes(seg)) return seg as ViewName;
  const legacy = window.location.hash.slice(1);
  return (VIEW_NAMES as string[]).includes(legacy) ? (legacy as ViewName) : null;
};

/** 画面ごとのパス。?device=phone などのクエリは保つ。 */
const pathFor = (view: ViewName): string => `/${view}${window.location.search}`;

const readHistoryIdx = (state: unknown): number =>
  typeof (state as { idx?: unknown } | null)?.idx === 'number' ? (state as { idx: number }).idx : 0;

/** 画面の中身。業務状態が読めているときだけ描く。 */
function Shell() {
  const { settings, degradedSince, updatedAt } = useBoardData();
  const { now } = useClock();
  const { ask } = useUi();
  const session = useSession();
  const logout = useLogout();
  const user = session.data?.user;
  const [view, setView] = useState<ViewName>('clerk');
  const viewRef = useRef<ViewName>('clerk');
  // 履歴上の位置。戻る／進むのどちらで来たかを見分け、スライドの向きを決める。
  const historyIdx = useRef<number>(readHistoryIdx(window.history.state));

  const role = user?.role ?? 'clerk';

  // 他の端末で状態が変わったらトーストで知らせる（大型モニタを含むすべての画面）
  useRemoteChangeNotices(role, Boolean(user));

  const navigate = useCallback(
    (
      next: ViewName | 'home',
      opts: { mode?: 'push' | 'replace'; direction?: NavDirection | null } = {},
    ) => {
      const mode = opts.mode ?? 'push';
      const target = next === 'home' ? homeView(role) : next;
      const allowed = canOpen(target, role) ? target : homeView(role);
      const direction =
        opts.direction !== undefined ? opts.direction : directionOf(viewRef.current, allowed);
      runPageTransition(direction, () => {
        viewRef.current = allowed;
        setView(allowed);
        window.scrollTo(0, 0);
      });
      const url = pathFor(allowed);
      const current = `${window.location.pathname}${window.location.search}`;
      // 利用者の操作は履歴に積み（戻るで前の画面へ）、URLの正規化は履歴を増やさない
      if (mode === 'push') {
        if (current !== url || window.location.hash) {
          historyIdx.current += 1;
          window.history.pushState({ idx: historyIdx.current }, '', url);
        }
      } else {
        window.history.replaceState({ idx: historyIdx.current }, '', url);
      }
    },
    [role],
  );

  // 起動時はURLに画面を合わせ、戻る／進むでも追従する
  useEffect(() => {
    navigate(readPath() ?? 'home', { mode: 'replace', direction: null });
    const onPop = (e: PopStateEvent) => {
      const idx = readHistoryIdx(e.state);
      const direction: NavDirection = idx < historyIdx.current ? 'back' : 'forward';
      historyIdx.current = idx;
      // iOS のスワイプで戻ったときはブラウザ自身が動かすので、二重に動かさない
      const uaAnimated =
        (e as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition === true;
      navigate(readPath() ?? 'home', { mode: 'replace', direction: uaAnimated ? null : direction });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [navigate]);

  // 大型モニタは Esc で自分の部署の画面へ戻す
  useEffect(() => {
    if (view !== 'monitor') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.scrim')) navigate('home');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, navigate]);

  // 表示中の画面名。大型モニタでは離れて見るため、トーストを大きく出す（app.css）
  useEffect(() => {
    document.documentElement.dataset.view = view;
  }, [view]);

  useEffect(() => {
    document.body.classList.toggle('is-degraded', Boolean(degradedSince));
  }, [degradedSince]);

  const showCalls = useCallback(() => {
    void ask({
      title: '電話で連絡する',
      text: degradedSince
        ? 'アプリの表示が不安定なあいだは、電話運用に切り替えてください。'
        : undefined,
      ok: '',
      cancel: '閉じる',
      body: (
        <div className="calls">
          {settings.ext.map((x) => (
            <a key={x.label} href={`tel:${x.no}`}>
              <Icon name="call" />
              <span>{x.label}</span>
              <b>{x.no}</b>
            </a>
          ))}
        </div>
      ),
    });
  }, [ask, degradedSince, settings.ext]);

  const readonly = useMemo(() => isReadonly(view, role), [view, role]);

  if (!user) return null;

  const isPhoneWindow = new URLSearchParams(window.location.search).get('device') === 'phone';
  // デモ・検証用の操作列。本番ビルドでは VITE_DEMO_TOOLS を付けないので出ない。
  const demoEnabled = import.meta.env.DEV || import.meta.env.VITE_DEMO_TOOLS === '1';
  const showDemo = demoEnabled && view !== 'monitor' && !isPhoneWindow;

  return (
    <>
      <div id="app" className={readonly ? 'readonly' : undefined}>
        {view === 'monitor' ? null : (
          <Header
            user={user}
            now={now}
            updatedAt={updatedAt}
            onNavigate={navigate}
            onCalls={showCalls}
            onLogout={() => logout.mutate()}
          />
        )}

        {degradedSince && view !== 'monitor' ? (
          <DegradedBar since={degradedSince} now={now} onCalls={showCalls} />
        ) : null}

        {view === 'clerk' ? <ClerkView /> : null}
        {view === 'rx' ? <RxView /> : null}
        {view === 'nurse' ? <NurseView /> : null}
        {view === 'board' ? <BoardView /> : null}
        {view === 'stats' ? <StatsView /> : null}
        {view === 'admin' ? <AdminView /> : null}
        {view === 'monitor' ? <MonitorView onClose={() => navigate('home')} /> : null}
      </div>

      {showDemo ? <DemoBar role={role} degraded={Boolean(degradedSince)} /> : null}
    </>
  );
}

export function App() {
  return (
    <>
      <AutoUpdate />
      <Screens />
    </>
  );
}

function Screens() {
  const session = useSession();
  const user = session.data?.user ?? null;
  const board = useBoard(Boolean(user));

  if (session.isLoading) return null;
  if (!user) return <LoginView entra={session.data?.authKind === 'entra'} />;
  if (!board.data) return null;

  return (
    <BoardProvider data={board.data}>
      <Shell />
    </BoardProvider>
  );
}
